import { useCallback, useEffect, useState } from 'react';
import {
	PreworkGraphicalViewResponse,
	PreworkCandidateFilter,
} from '../../../shared/PreWorkServiceTypes';
import {
	GraphViewListState,
	CandidatesBatchState,
} from './CandidatesList.store';
import {
	COUNTRY_CODE_INDIA,
	PreworkGroupTabs,
} from '../../../shared/Constants';
import { useAuth } from '../../../hooks/useAuth';
import { useLoadingIndicator } from '../../../hooks/useLoadingIndicator';
import { useToastMessage } from '../../../hooks/useToastMessage';
import useQueryParams from '../../../hooks/useQueryParams';
import { getPreworkGraphicalView } from '../../../service/PreWorkService';
import { useSnapshot } from 'valtio';
import useUserSession from '../../../hooks/useUserSession';
import { UserRole } from '../../../shared/VolunteerApplicationServiceTypes';

export const useGraphViewPrework = () => {
	const { srcmId } = useAuth();
	const { selectedRole } = useUserSession();
	const { setLoading } = useLoadingIndicator();
	const { showToastMessage } = useToastMessage();
	const queryParams = useQueryParams();
	const tabKey = queryParams.get('preworkActiveTab');
	const candidateBatchSnap = useSnapshot(CandidatesBatchState);
	const selectedBatchId = candidateBatchSnap.selectedBatch?.id;
	const [graphViewData, setGraphViewData] =
		useState<PreworkGraphicalViewResponse>();
	const [graphViewFilter, setGraphViewFilter] =
		useState<PreworkCandidateFilter>({
			showAllCandidates: true,
			countryIds: [COUNTRY_CODE_INDIA],
			batchId: selectedBatchId,
		});

	useEffect(() => {
		if (candidateBatchSnap) {
			setGraphViewFilter((prevState) => ({
				...prevState,
				batchId: selectedBatchId,
			}));
		}
	}, [candidateBatchSnap, selectedBatchId]);

	const onGraphViewFilterChange = useCallback(
		(filterParam: PreworkCandidateFilter) => {
			GraphViewListState.filterBy = { ...filterParam };
			setGraphViewFilter(filterParam);
		},
		[]
	);

	const fetchGraphViewData = useCallback(
		async (filter: PreworkCandidateFilter) => {
			if (srcmId && filter.batchId) {
				setLoading(true);
				try {
					const graphData = await getPreworkGraphicalView({
						functionarySrcmId: srcmId,
						selectedRoleCode: selectedRole as UserRole,
						candidateFilter: filter,
					});
					setGraphViewData(graphData);
				} catch (e) {
					if (e instanceof Error) {
						showToastMessage('error', e.message);
					} else {
						showToastMessage('error', 'Unknown error occurred');
					}
				} finally {
					setLoading(false);
				}
			}
		},
		[srcmId, setLoading, selectedRole, showToastMessage]
	);

	useEffect(() => {
		if (tabKey === PreworkGroupTabs.GRAPHICAL_VIEW) {
			void fetchGraphViewData(graphViewFilter);
		}
	}, [graphViewFilter, fetchGraphViewData, tabKey]);

	return {
		graphViewData,
		graphViewFilter,
		onGraphViewFilterChange,
	};
};
